/**
 * CollectionForm.jsx — Muhasebe FAZ 3.4/3.6 (PLAN §3.4/§11.2) + F1 fix
 * (fix/tahsilat-taksit-baglama, satır-bazlı "taksit modu").
 *
 * Sheet içinde admin manuel tahsilat/peşinat formu. react-hook-form + zod
 * (KURULU paketler — yeni bağımlılık eklenmedi).
 *
 * KİLİTLİ KARAR: peşinat (`kind="down_payment"`) admin'de işlenir, danışmanda
 * DEĞİL. Bu form yalnızca admin ekranında (AdminStudentDetailPage → Muhasebe →
 * Borç & Tahsilat) kullanılır — danışman ekranlarına taşınmadı/eklenmedi.
 *
 * `bank_id` her ödeme yönteminde ZORUNLU (backend kırıcı değişikliği, bkz.
 * routes/payments.py::admin_add_student_payment). `muhasebe_tipi` serbest
 * metin DEĞİL — definitions(type=muhasebe_tipi)'den seçilir (hard-code YOK).
 *
 * Çift gönderim koruması: `isSubmitting` (RHF) + mutation.isPending ikisi de
 * submit butonunu disable eder; para işlemi iki kez kaydedilmemeli.
 *
 * ── İKİ MOD (F1 fix) ─────────────────────────────────────────────────────────
 * `installmentRow` prop'u dolu geldiğinde form "taksit modu"na geçer:
 *   - amount taksidin `remaining`ine SABİTTİR, kullanıcı DEĞİŞTİREMEZ (backend
 *     kısmi tahsilat kabul etmiyor — POST /admin/installments/{id}/collect
 *     `taksit_tahsilat_tutari_eslesmiyor` ile 400 döner).
 *   - `kind`/`course_id` alanları YOK (taksit zaten belirli bir faturaya/
 *     kursa bağlı — backend bunları body'de kabul etmiyor).
 *   - submit `useCollectInstallment` ile BU taksidin invoice_id'sine gider —
 *     `useAddManualPayment`'ın (invoice_id'yi kendisi seçen, en eski açık
 *     faturaya düşen) AKSİNE her zaman doğru faturaya bağlanır.
 * `installmentRow` boşsa (null/undefined) davranış ÖNCEKİ gibi — genel/fatura
 * modu, `useAddManualPayment` ile `/admin/students/{id}/add-payment`.
 *
 * ── invoiceRow (F1-B fix, reviewer Yüksek bulgusu) ──────────────────────────
 * `installmentRow` boşken `invoiceRow` doluysa (taksitsiz/row_type="invoice"
 * bir borç satırından açıldıysa) genel/fatura moduyla AYNI form kullanılır
 * (kind/course_id alanları kalır) ama iki fark vardır:
 *   - `amount` faturanın kalanıyla ÖN DOLDURULUR (düzenlenebilir — backend
 *     kısmi tahsilatı YASAKLAMIYOR, yalnızca tavanı aşamaz).
 *   - submit body'sine `invoice_id: invoiceRow.id` eklenir — backend'in
 *     "birden fazla açık fatura varsa en eskiyi seç" sessiz davranışı
 *     (routes/admin.py::admin_add_student_payment F1-B fix) artık DEVRE DIŞI,
 *     tahsilat DAİMA bu faturaya bağlanır. Tutar kalanı aşarsa istemci
 *     tarafında (backend'e gitmeden) hata gösterilir — bkz. buildSchema
 *     `maxAmount` parametresi.
 */
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage,
} from '@/components/ui/form';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorCode, getApiErrorMessage } from '@/utils/apiError';
import { fmtTRY } from '@/utils/formatCurrency';
import { useAddManualPayment } from '@/api/queries/students';
import { useAccountingBanks, useMuhasebeTipiOptions, useCollectInstallment } from '@/api/queries/accounting';
// POS hesabına tahsilatta "aktarılan tutar" zorunluluğu — CollectModal /
// NoteCollectModal (AdminAccounting.jsx) ile AYNI kural, tek yerde tutulur.
import { isSelectedBankPos, validateNetAmount, netAmountErrorI18nKey, computeCommission } from '@/lib/posNetAmount';

const INPUT_CLASS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-[var(--bg-app)] text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none disabled:opacity-50';

// ── zod schema (i18n mesajlı — t() fonksiyonuna bağlı, factory) ──────────────
// `banks` — seçilen bank_id'nin account_type'ını (POS mu) çözmek için gerekir;
// POS hesabına tahsilatta net_amount ZORUNLU hale gelir (bkz. lib/posNetAmount.js).
// `maxInvoiceAmount` (F1-B fix, opsiyonel) — satır bazlı "Tahsilat" belirli
// bir taksitsiz açık faturadan (row_type="invoice") açıldıysa dolar: tutarın
// faturanın kalanını AŞAMAYACAĞI istemci tarafında doğrulanır (backend'in
// `tahsilat_tutari_kalani_asiyor` 400'ünün ayna kontrolü — round-trip yerine
// anında geri bildirim). Diğer tüm çağrı yollarında (genel mod, invoiceRow
// yok) `null` — davranış DEĞİŞMEDİ.
function buildSchema(t, banks, maxInvoiceAmount = null) {
  return z
    .object({
      amount: z.coerce.number().positive(t('accounting.collect_form.errors.amount_invalid')),
      bank_id: z.string().min(1, t('accounting.collect_form.errors.bank_required')),
      method: z.enum(['cash', 'bank_transfer', 'postal', 'credit_card']),
      kind: z.enum(['manual', 'down_payment']),
      course_id: z.string().optional().default(''),
      muhasebe_tipi: z.string().optional().default(''),
      vat_override_amount: z.string().optional().default(''),
      net_amount: z.string().optional().default(''),
      description: z.string().optional().default(''),
    })
    .superRefine((data, ctx) => {
      if (data.vat_override_amount === '' || data.vat_override_amount == null) return;
      const vatNum = Number(data.vat_override_amount);
      const amountNum = Number(data.amount) || 0;
      if (Number.isNaN(vatNum) || vatNum < 0 || vatNum > amountNum + 0.005) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vat_override_amount'],
          message: t('accounting.collect_form.errors.vat_exceeds_amount'),
        });
      }
    })
    .superRefine((data, ctx) => {
      const amountNum = Number(data.amount) || 0;
      const isPos = isSelectedBankPos(banks, data.bank_id);
      const errorKey = validateNetAmount({ amount: amountNum, netAmountRaw: data.net_amount, isPos });
      if (errorKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['net_amount'],
          message: t(netAmountErrorI18nKey(errorKey)),
        });
      }
    })
    .superRefine((data, ctx) => {
      if (maxInvoiceAmount == null) return;
      const amountNum = Number(data.amount) || 0;
      if (amountNum > maxInvoiceAmount + 0.005) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amount'],
          message: t('accounting.collect_form.errors.amount_exceeds_invoice_remaining'),
        });
      }
    });
}

// ── "Taksit modu" şeması (F1 fix) ────────────────────────────────────────────
// `amount` burada FORM ALANI DEĞİL — taksidin `remaining`i (fixedAmount) ile
// sabit, salt-okunur gösterilir. `kind`/`course_id` yok (bkz. dosya başı notu).
function buildInstallmentSchema(t, banks, fixedAmount) {
  return z
    .object({
      bank_id: z.string().min(1, t('accounting.collect_form.errors.bank_required')),
      method: z.enum(['cash', 'bank_transfer', 'postal', 'credit_card']),
      muhasebe_tipi: z.string().optional().default(''),
      vat_override_amount: z.string().optional().default(''),
      net_amount: z.string().optional().default(''),
      description: z.string().optional().default(''),
    })
    .superRefine((data, ctx) => {
      if (data.vat_override_amount === '' || data.vat_override_amount == null) return;
      const vatNum = Number(data.vat_override_amount);
      if (Number.isNaN(vatNum) || vatNum < 0 || vatNum > fixedAmount + 0.005) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vat_override_amount'],
          message: t('accounting.collect_form.errors.vat_exceeds_amount'),
        });
      }
    })
    .superRefine((data, ctx) => {
      const isPos = isSelectedBankPos(banks, data.bank_id);
      const errorKey = validateNetAmount({ amount: fixedAmount, netAmountRaw: data.net_amount, isPos });
      if (errorKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['net_amount'],
          message: t(netAmountErrorI18nKey(errorKey)),
        });
      }
    });
}

/** Backend error_code -> yerel `accounting.errors.*` çevirisi, yoksa backend'in
 *  kendi message_tr/message_en'i (getApiErrorMessage) fallback olarak kullanılır. */
function collectionErrorMessage(err, lang, t) {
  const code = getApiErrorCode(err);
  if (code) {
    const key = `accounting.errors.${code}`;
    const localized = t(key);
    if (localized !== key) return localized;
  }
  return getApiErrorMessage(err, lang);
}

export default function CollectionForm({
  open, onOpenChange, userId, enrollments, initialCourseId, installmentRow, invoiceRow,
}) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const addPayment = useAddManualPayment(userId);
  const collectInstallment = useCollectInstallment(userId);
  const { data: banks = [], isLoading: banksLoading } = useAccountingBanks();
  const { data: muhasebeTipiOptions = [], isLoading: defsLoading } = useMuhasebeTipiOptions();

  // F1 fix — "taksit modu": bkz. dosya başı notu.
  const isInstallmentMode = !!installmentRow;
  const fixedAmount = installmentRow?.remaining ?? 0;
  // F1-B fix — belirli bir taksitsiz açık faturadan (row_type="invoice")
  // açıldıysa: genel/fatura modu, ama tutar ön-dolu + tavanı `invoiceRow.
  // remaining`. `installmentRow` varsa BU YOK SAYILIR (taksit modu önceliklidir,
  // ikisi aynı anda anlamlı değil — DebtLedgerSubTab ikisini birlikte set etmez).
  const isInvoiceRowMode = !isInstallmentMode && !!invoiceRow;
  const invoiceRemaining = invoiceRow?.remaining ?? 0;

  const schema = useMemo(
    () =>
      isInstallmentMode
        ? buildInstallmentSchema(t, banks, fixedAmount)
        : buildSchema(t, banks, isInvoiceRowMode ? invoiceRemaining : null),
    [isInstallmentMode, fixedAmount, isInvoiceRowMode, invoiceRemaining, t, banks]
  );
  const defaultValues = useMemo(
    () =>
      isInstallmentMode
        ? {
            bank_id: '',
            method: 'cash',
            muhasebe_tipi: '',
            vat_override_amount: '',
            net_amount: '',
            description: '',
          }
        : {
            amount: isInvoiceRowMode ? invoiceRemaining : '',
            bank_id: '',
            method: 'cash',
            kind: 'manual',
            course_id: (isInvoiceRowMode ? invoiceRow?.course_id : '') || initialCourseId || '',
            muhasebe_tipi: '',
            vat_override_amount: '',
            net_amount: '',
            description: '',
          },
    [isInstallmentMode, isInvoiceRowMode, invoiceRemaining, invoiceRow, initialCourseId]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Sheet her açıldığında formu sıfırla (önceki tahsilat kalıntısı kalmasın).
  useEffect(() => {
    if (open) form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // POS hesabına tahsilatta net_amount zorunlu — canlı komisyon satırı ve
  // zorunlu etiket için seçili bankanın türü/tutar izlenir. Taksit modunda
  // tutar sabit (fixedAmount), kullanıcı girmiyor.
  const watchedBankId = form.watch('bank_id');
  const watchedAmount = isInstallmentMode ? fixedAmount : form.watch('amount');
  const watchedNetAmount = form.watch('net_amount');
  const isPosAccount = isSelectedBankPos(banks, watchedBankId);
  const commission = computeCommission(Number(watchedAmount) || 0, watchedNetAmount);

  const courseOptions = (enrollments || []).map((e) => ({
    course_id: e.course_id,
    label: e.course_title || e.title || e.course_id,
  }));

  const onSubmit = async (values) => {
    if (isInstallmentMode) {
      const payload = {
        amount: fixedAmount,
        payment_method: values.method,
        bank_id: values.bank_id,
        vat_override_amount: values.vat_override_amount === '' ? null : Number(values.vat_override_amount),
        net_amount: values.net_amount === '' ? null : Number(values.net_amount),
        muhasebe_tipi: values.muhasebe_tipi || null,
        description: values.description || '',
      };
      try {
        await collectInstallment.mutateAsync({ installmentId: installmentRow.id, payload });
        toast.success(t('accounting.collect_form.success'));
        onOpenChange(false);
      } catch (err) {
        toast.error(collectionErrorMessage(err, lang, t));
      }
      return;
    }
    const payload = {
      amount: Number(values.amount),
      method: values.method,
      description: values.description || '',
      course_id: values.course_id || '',
      bank_id: values.bank_id,
      kind: values.kind,
      vat_override_amount: values.vat_override_amount === '' ? null : Number(values.vat_override_amount),
      net_amount: values.net_amount === '' ? null : Number(values.net_amount),
      muhasebe_tipi: values.muhasebe_tipi || null,
      // F1-B fix — invoiceRow doluysa tahsilat DAİMA bu faturaya bağlanır
      // (backend'in "birden fazla açık fatura -> en eskiyi seç" sessiz
      // davranışı devre dışı kalır). invoiceRow yoksa `undefined` — body'ye
      // hiç eklenmez, eski davranış (tek açık fatura varsa ona otomatik
      // bağlanır) DEĞİŞMEZ.
      ...(isInvoiceRowMode ? { invoice_id: invoiceRow.id } : {}),
    };
    try {
      await addPayment.mutateAsync(payload);
      toast.success(t('accounting.collect_form.success'));
      onOpenChange(false);
    } catch (err) {
      toast.error(collectionErrorMessage(err, lang, t));
    }
  };

  const submitting = form.formState.isSubmitting || addPayment.isPending || collectInstallment.isPending;

  return (
    <Sheet open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-white" data-testid="collection-form-sheet">
        <SheetHeader>
          <SheetTitle className="font-display">
            {isInstallmentMode ? t('accounting.collect_form.installment_title') : t('accounting.collect_form.title')}
          </SheetTitle>
          <SheetDescription>
            {isInstallmentMode ? t('accounting.collect_form.installment_subtitle') : t('accounting.collect_form.subtitle')}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4" noValidate>
            {/* Tutar — taksit modunda salt-okunur (backend kısmi tahsilat kabul
                etmiyor), aksi halde normal düzenlenebilir alan. */}
            {isInstallmentMode ? (
              <div>
                <label className="block text-sm font-bold text-[var(--text-main)] mb-1">
                  {t('accounting.collect_form.amount_label')}
                </label>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={fmtTRY(fixedAmount)}
                  className={`${INPUT_CLASS} cursor-not-allowed`}
                  data-testid="collection-amount-input"
                />
                <p className="text-[11px] text-[var(--text-muted)] mt-1">
                  {t('accounting.collect_form.installment_amount_readonly_hint')}
                </p>
              </div>
            ) : (
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('accounting.collect_form.amount_label')}</FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        type="number"
                        step="0.01"
                        min="0"
                        className={INPUT_CLASS}
                        disabled={submitting}
                        data-testid="collection-amount-input"
                      />
                    </FormControl>
                    {/* F1-B fix — belirli bir faturadan açıldıysa (isInvoiceRowMode)
                        kullanıcıya tutarın ön-dolu VE tavanlı olduğu hatırlatılır. */}
                    {isInvoiceRowMode && (
                      <FormDescription data-testid="collection-invoice-amount-hint">
                        {t('accounting.collect_form.invoice_amount_hint').replace(
                          '{remaining}',
                          fmtTRY(invoiceRemaining)
                        )}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Banka — HER YÖNTEMDE ZORUNLU (kırıcı değişiklik) */}
            <FormField
              control={form.control}
              name="bank_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounting.collect_form.bank_label')} *</FormLabel>
                  <FormControl>
                    <select {...field} className={INPUT_CLASS} disabled={submitting || banksLoading} data-testid="collection-bank-select">
                      <option value="">{t('accounting.collect_form.bank_placeholder')}</option>
                      {banks.map((b) => (
                        <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>{t('accounting.collect_form.bank_required_hint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Ödeme yöntemi */}
            <FormField
              control={form.control}
              name="method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounting.collect_form.method_label')}</FormLabel>
                  <FormControl>
                    <select {...field} className={INPUT_CLASS} disabled={submitting} data-testid="collection-method-select">
                      <option value="cash">{t('accounting.methods.cash')}</option>
                      <option value="bank_transfer">{t('accounting.methods.bank_transfer')}</option>
                      <option value="postal">{t('accounting.methods.postal')}</option>
                      <option value="credit_card">{t('accounting.methods.credit_card')}</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hesaba geçen tutar (net) — prim yine brüt (amount) üzerinden hesaplanır.
                POS hesabına tahsilatta ZORUNLU hale gelir (bkz. lib/posNetAmount.js). */}
            <FormField
              control={form.control}
              name="net_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isPosAccount
                      ? t('accounting.collect_form.net_settled_amount_required_label')
                      : t('accounting.fields.net_settled_amount.label')}
                    {isPosAccount && <span className="text-red-500"> *</span>}
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      aria-required={isPosAccount ? 'true' : undefined}
                      className={INPUT_CLASS}
                      disabled={submitting}
                      data-testid="collection-net-amount-input"
                    />
                  </FormControl>
                  {isPosAccount ? (
                    <FormDescription>
                      {t('accounting.collect_form.net_settled_amount_help')}{' '}
                      <strong className="font-bold text-[var(--text-main)]">
                        {t('accounting.collect_form.net_settled_amount_help_emphasis')}
                      </strong>
                    </FormDescription>
                  ) : (
                    <FormDescription>{t('accounting.collect_form.net_settled_amount_optional_hint')}</FormDescription>
                  )}
                  {isPosAccount && commission !== null && (
                    <p data-testid="collection-net-amount-commission" className="text-[11px] font-bold text-amber-700">
                      {t('accounting.collect_form.commission_label')}: {fmtTRY(commission)}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tahsilat türü (manuel | peşinat) — taksit modunda YOK: taksit
                zaten belirli bir faturaya/kursa bağlı, backend bu alanları
                kabul etmiyor (bkz. dosya başı notu). */}
            {!isInstallmentMode && (
              <FormField
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('accounting.collect_form.kind_label')}</FormLabel>
                    <FormControl>
                      <select {...field} className={INPUT_CLASS} disabled={submitting} data-testid="collection-kind-select">
                        <option value="manual">{t('accounting.kind.manual')}</option>
                        <option value="down_payment">{t('accounting.kind.down_payment')}</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Eğitim (opsiyonel) — taksit modunda YOK, bkz. yukarıdaki not. */}
            {!isInstallmentMode && (
              <FormField
                control={form.control}
                name="course_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('accounting.collect_form.course_label')}</FormLabel>
                    <FormControl>
                      <select {...field} className={INPUT_CLASS} disabled={submitting} data-testid="collection-course-select">
                        <option value="">{t('accounting.collect_form.course_general')}</option>
                        {courseOptions.map((c) => (
                          <option key={c.course_id} value={c.course_id}>{c.label}</option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Muhasebe tipi — definitions(type=muhasebe_tipi)'den, HARD-CODE YOK */}
            <FormField
              control={form.control}
              name="muhasebe_tipi"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounting.collect_form.muhasebe_tipi_label')}</FormLabel>
                  <FormControl>
                    <select {...field} className={INPUT_CLASS} disabled={submitting || defsLoading} data-testid="collection-muhasebe-tipi-select">
                      <option value="">{t('accounting.collect_form.muhasebe_tipi_none')}</option>
                      {muhasebeTipiOptions.map((d) => (
                        <option key={d.def_id || d.value} value={d.value}>
                          {lang === 'tr' ? d.label_tr : (d.label_en || d.label_tr)}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* KDV tutarı (elle düzeltme, opsiyonel) */}
            <FormField
              control={form.control}
              name="vat_override_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounting.collect_form.vat_override_label')}</FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      className={INPUT_CLASS}
                      disabled={submitting}
                      data-testid="collection-vat-override-input"
                    />
                  </FormControl>
                  <FormDescription>{t('accounting.collect_form.vat_override_help')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Açıklama */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounting.collect_form.description_label')}</FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      type="text"
                      placeholder={t('accounting.collect_form.description_placeholder')}
                      className={INPUT_CLASS}
                      disabled={submitting}
                      data-testid="collection-description-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter className="pt-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
              >
                {t('admin.btn_cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2.5 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="collection-submit-btn"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? t('accounting.collect_form.submitting') : t('accounting.collect_form.submit')}
              </button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
